import React, { useEffect, useState } from "react";
import CommonButton from "../../../components/CommonButton";
import TextInputComp from "../../../components/customer/TextInputComp";
import Dropdown, { Option } from "../../../components/customer/Dropdown";
import TextAreaComp from "../../../components/customer/TextAreaComp";
import {
  useCreatecarrierMutation,
  useGetAllJobsByIdQuery,
  useUpdatecarrierMutation,
} from "../../../redux/actions/Admin/footerManagementAdmin";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Star } from "lucide-react";
import {
  ICreateCarrierRequest,
  IUpdateCarrierRequest,
} from "../../../types/Request/AdminRequest";

const AddNewCareersScreen: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  // Mutation hooks unpacking structures setup
  const [createCarrier, { isLoading: isCreating }] = useCreatecarrierMutation();
  const [updateCarrier, { isLoading: isUpdating }] = useUpdatecarrierMutation();

  // Explicit retrieval query hooks
  const { data: jobData, isLoading: isLoadingDetails } = useGetAllJobsByIdQuery(
    Number(id),
    {
      skip: !id,
    }
  );

  const industryOptions: Option[] = [
    { id: "1", label: "Engineering", value: "engineering" },
    { id: "2", label: "Design", value: "design" },
    { id: "3", label: "Marketing", value: "marketing" },
    { id: "4", label: "Custom", value: "custom" },
  ];

  const jobOptions: Option[] = [
    { id: "1", label: "Full Time", value: "fullTime" },
    { id: "2", label: "Part Time", value: "partTime" },
    { id: "3", label: "Contract", value: "contract" },
  ];

  const [selectedSort, setSelectedSort] = useState<Option | null>(
    industryOptions[0]
  );
  const [selectedJobType, setSelectedJobType] = useState<Option | null>(
    jobOptions[0]
  );

  const [newJob, setNewJob] = useState({
    title: "",
    location: "",
    experienceLevel: "",
    salaryRange: "",
    vacancyCount: 1,
    description: "",
    responsibilities: "",
    requirements: "",
    benefits: "",
    isFeatured: false,
  });

  const isProcessing = isCreating || isUpdating || isLoadingDetails;

  useEffect(() => {
    const unwrappedJob = jobData;
    if (unwrappedJob) {
      setNewJob({
        title: unwrappedJob.value?.data?.jobTitle || "",
        location: unwrappedJob.value?.data?.location || "",
        experienceLevel: String(
          unwrappedJob.value?.data?.experienceLevel || ""
        ),
        salaryRange: unwrappedJob.value?.data?.salaryRange || "",
        vacancyCount: unwrappedJob.value?.data?.vacancyCount || 1,
        description: unwrappedJob.value?.data?.description || "",
        responsibilities: unwrappedJob.value?.data?.responsibilities || "",
        requirements: unwrappedJob.value?.data?.requirements || "",
        benefits: unwrappedJob.value?.data?.benefits || "",
        isFeatured: unwrappedJob.value?.data?.isFeatured || false,
      });

      const matchedInd = industryOptions.find(
        (o) =>
          o.label?.toLowerCase() ===
          unwrappedJob.value?.data?.department?.toLowerCase()
      );
      if (matchedInd) setSelectedSort(matchedInd);

      const matchedType = unwrappedJob.value?.data?.employmentType
        ?.toLowerCase()
        .includes("full")
        ? jobOptions[0]
        : unwrappedJob.value?.data?.employmentType
            ?.toLowerCase()
            .includes("part")
        ? jobOptions[1]
        : jobOptions[2];
      setSelectedJobType(matchedType);
    }
  }, [jobData]);

  const handleCreateOpening = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newJob.title.trim() || isProcessing) return;

    // FIX: Formats target date as YYYY-MM-DD exactly (e.g., 2026-07-30)
    const targetDateFutureObj = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
    const formattedDateString = targetDateFutureObj.toISOString().split("T")[0];

    const commonPayload = {
      jobTitle: newJob.title.trim(),
      department: selectedSort?.label || "Engineering",
      location: newJob.location.trim(),
      employmentType:
        selectedJobType?.value === "fullTime"
          ? 1
          : selectedJobType?.value === "partTime"
          ? 2
          : 3,
      experienceLevel: Number(newJob.experienceLevel) || 1,
      salaryRange: newJob.salaryRange.trim(),
      vacancyCount: Number(newJob.vacancyCount) || 1,
      description: newJob.description.trim(),
      responsibilities: newJob.responsibilities.trim(),
      requirements: newJob.requirements.trim(),
      benefits: newJob.benefits.trim(),
      lastApplyDate: formattedDateString, // Transmitting clean date string mapping
      isActive: true,
      isFeatured: newJob.isFeatured,
    };

    try {
      if (isEditMode) {
        await updateCarrier({
          id: Number(id),
          ...commonPayload,
        } as IUpdateCarrierRequest).unwrap();
      } else {
        await createCarrier(commonPayload as ICreateCarrierRequest).unwrap();
      }
      navigate(-1);
    } catch (error) {
      console.error("Failed to save job.", error);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 min-h-screen bg-[#FAFBF8] relative">
      {isProcessing && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-6 flex items-center gap-3 shadow-xl">
            <Loader2 className="w-5 h-5 animate-spin text-[#2F5E2E]" />
            <span className="font-semibold text-sm text-slate-700">
              Saving job details...
            </span>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          disabled={isProcessing}
          className="p-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition cursor-pointer disabled:opacity-40"
          type="button"
        >
          <ArrowLeft size={16} />
        </button>

        <div className="text-left">
          <h1 className="text-2xl font-black text-logoHeading">
            {isEditMode ? "Edit Job Opening" : "Create Job Opening"}
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Fill in the job details below.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto bg-white border border-gray-200 rounded-3xl p-5 shadow-xs">
        <form onSubmit={handleCreateOpening} className="space-y-5 text-left">
          <TextInputComp
            showLabel
            placeholder="e.g. Frontend Developer"
            value={newJob.title}
            onChangeText={(text) => setNewJob({ ...newJob, title: text })}
            label="Job Title"
            showStar
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Dropdown
              options={industryOptions}
              value={selectedSort}
              onChange={(option) => setSelectedSort(option)}
              className="w-full"
              innerClassName="bg-gray-50 border-gray-200 font-bold text-xs !py-2"
              iconPosition="none"
              showLabel
              showStar
              label="Department"
            />
            <Dropdown
              options={jobOptions}
              value={selectedJobType}
              onChange={(option) => setSelectedJobType(option)}
              className="w-full"
              innerClassName="bg-gray-50 border-gray-200 font-bold text-xs !py-2"
              iconPosition="none"
              showLabel
              showStar
              label="Employment Type"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInputComp
              showLabel
              placeholder="e.g. Dubai, UAE or Remote"
              value={newJob.location}
              onChangeText={(text) => setNewJob({ ...newJob, location: text })}
              label="Job Location"
              showStar
            />
            <TextInputComp
              showLabel
              placeholder="e.g. 3 Years"
              value={newJob.experienceLevel}
              onChangeText={(text) =>
                setNewJob({ ...newJob, experienceLevel: text })
              }
              label="Experience Required (Years)"
              showStar
            />
          </div>

          <TextAreaComp
            showLabel
            label="Required Skills"
            showStar
            placeholder="Enter the skills required for this job..."
            value={newJob.requirements}
            onChangeText={(text) =>
              setNewJob({ ...newJob, requirements: text })
            }
          />

          <TextAreaComp
            showLabel
            label="Job Responsibilities"
            showStar
            placeholder="List functional team task workflows execution steps items lines..."
            value={newJob.responsibilities}
            onChangeText={(text) =>
              setNewJob({ ...newJob, responsibilities: text })
            }
          />

          <TextAreaComp
            showLabel
            label="Job Description"
            showStar
            placeholder="Write a short description of the job..."
            value={newJob.description}
            onChangeText={(text) => setNewJob({ ...newJob, description: text })}
          />

          <TextAreaComp
            showLabel
            label="Benefits & Perks"
            placeholder="List the benefits offered for this job..."
            value={newJob.benefits}
            onChangeText={(text) => setNewJob({ ...newJob, benefits: text })}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInputComp
              showLabel
              label="Salary Range"
              showStar
              placeholder="e.g. AED 18,000 - 24,000 / Month"
              value={newJob.salaryRange}
              onChangeText={(text) =>
                setNewJob({ ...newJob, salaryRange: text })
              }
            />
            <TextInputComp
              showLabel
              label="Number of Open Positions"
              showStar
              placeholder="1"
              value={String(newJob.vacancyCount)}
              onChangeText={(text) =>
                setNewJob({ ...newJob, vacancyCount: Number(text) || 1 })
              }
            />
          </div>

          <div className="flex items-center select-none bg-[#FAFBF8] border p-3.5 rounded-xl border-dashed border-slate-200 w-fit cursor-pointer">
            <input
              id="isFeaturedToggle"
              type="checkbox"
              checked={newJob.isFeatured}
              onChange={(e) =>
                setNewJob({ ...newJob, isFeatured: e.target.checked })
              }
              className="w-4 h-4 text-[#2F5E2E] border-gray-300 rounded focus:ring-[#6DBE45] cursor-pointer"
            />
            <label
              htmlFor="isFeaturedToggle"
              className="ml-2.5 text-xs font-bold text-slate-700 cursor-pointer flex items-center gap-1"
            >
              <Star size={13} className="text-amber-500 fill-amber-500" />
              <span>Show this job as Featured</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <CommonButton
              text="Cancel"
              variant="outline"
              rounded="lg"
              disabled={isProcessing}
              onClick={() => navigate(-1)}
              className="px-5 text-xs font-semibold"
            />
            <CommonButton
              text={isEditMode ? "Save Changes" : "Create Job"}
              variant="filled"
              rounded="lg"
              disabled={isProcessing || !newJob.title.trim()}
              className="bg-[#2F5E2E] hover:bg-[#1f3f1e] text-white text-xs font-black px-5 py-2.5 shadow-sm"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddNewCareersScreen;
